import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Calendar, Send, Lock, CheckCircle, Info, Upload, X } from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../utils/schemaData';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './Contact.css';
import { useFormsSwitchedOff, PreviewFormNotice } from '../components/PreviewFormNotice';

const Contact = () => {
  // Breadcrumb Schema
  const breadcrumbs = breadcrumbSchema([
    { name: "Home", url: "https://www.clinicalminds.net/" },
    { name: "Contact", url: "https://www.clinicalminds.net/contact" }
  ]);
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const formsOff = useFormsSwitchedOff();
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({ clientName: '', contactEmail: '', notes: '', file: null });
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    message: '',
    bestTimeToContact: '',
    contactPreference: 'email',
    reasonForContact: ''
  });

  useEffect(() => {
    const enquiry = searchParams.get('enquiry');
    if (enquiry) {
      const mapping = {
        'autism': 'Autism assessment',
        'adhd': 'ADHD assessment',
        'fasd': 'FASD assessment',
        'learning': 'Learning assessment',
        'intellectual': 'Intellectual disability assessment',
        'therapy': 'Therapy',
        'school': 'School partnership',
        'referral': 'Referral from a health professional',
        'Assessment': 'Assessment'
      };
      const match = mapping[enquiry];
      if (match) {
        setFormData(prev => ({ ...prev, reasonForContact: match }));
      }
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Create FormData object (backend expects form data, not JSON)
      const formDataToSend = new FormData();
      formDataToSend.append('first_name', formData.firstName);
      formDataToSend.append('last_name', formData.lastName);
      formDataToSend.append('phone_number', formData.phoneNumber);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('best_time_to_contact', formData.bestTimeToContact);
      formDataToSend.append('contact_preference', formData.contactPreference);
      formDataToSend.append('reason_for_contact', formData.reasonForContact);

      const response = await fetch(`${backendUrl}/api/contact/submit`, {
        method: 'POST',
        body: formDataToSend
        // Note: Don't set Content-Type header - browser will set it automatically with boundary
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);
        // Reset form after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
          setFormData({
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: '',
            message: '',
            bestTimeToContact: '',
            contactPreference: 'email',
            reasonForContact: ''
          });
        }, 5000);
      } else {
        // Handle error message - could be string, array, or object
        let errorMsg = 'Submission failed. Please try again.';
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(err => err.msg || err).join(', ');
        } else if (data.message) {
          errorMsg = data.message;
        }
        setSubmitError(errorMsg);
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
      console.error('Contact form error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    if (!uploadData.file) { setUploadError('Please select a file to upload.'); return; }
    setUploading(true);
    setUploadError('');
    try {
      const body = new FormData();
      body.append('client_name', uploadData.clientName);
      body.append('contact_email', uploadData.contactEmail);
      body.append('notes', uploadData.notes);
      body.append('file', uploadData.file);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/client/upload`, { method: 'POST', body });
      const data = await response.json();
      if (response.ok && data.success) {
        setUploadSuccess(true);
        setTimeout(() => {
          setUploadSuccess(false);
          setShowUploadModal(false);
          setUploadData({ clientName: '', contactEmail: '', notes: '', file: null });
        }, 3000);
      } else {
        setUploadError(data.error || 'Upload failed. Please try again.');
      }
    } catch (error) {
      setUploadError('Network error. Please check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="page-contact">
      <SEO 
        title="Contact Clinical Psychologist (Registrar) Coffs Harbour | Clinical Minds"
        description="Contact Lachlan Beaton, Clinical Psychologist (Registrar) in Coffs Harbour. Phone: 02 6600 2710. Email: contact@clinicalminds.net. Book appointments or ask questions."
        canonicalUrl="https://www.clinicalminds.net/contact"
        schemaData={breadcrumbs}
      />
      {/* Scroll Indicator for Mobile */}
      <ScrollIndicator />
      
      {/* Hero Section */}
      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">Contact</span>
          <h1 className="heading-hero">Contact Us</h1>
          <p className="hero-subline">We're here to answer your questions and support your journey</p>
        </div>
      </section>

      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <div className="card info-section">
                <h2 className="heading-2">Contact Information</h2>
                <p className="body-medium">
                  Have questions or need assistance? Reach out to us using any of the methods below.
                </p>
                
                <div className="contact-methods">
                  <div className="contact-method">
                    <div className="method-icon">
                      <Phone size={24} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="heading-3">Phone</h3>
                      <p className="body-medium">
                        <strong>Main Office:</strong> <a href="tel:0266002710">02 6600 2710</a>
                      </p>
                      <p className="body-medium">
                        <strong>SMS:</strong> <a href="sms:0415220944">0415 220 944</a>
                      </p>
                      <p className="body-medium">
                        <strong>Fax:</strong> <a href="tel:0266991457">02 6699 1457</a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="contact-method">
                    <div className="method-icon">
                      <Mail size={24} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="heading-3">Email</h3>
                      <p className="body-medium">
                        <a href="mailto:contact@clinicalminds.net">contact@clinicalminds.net</a>
                      </p>
                      <p className="body-small">We respond within 24 hours</p>
                    </div>
                  </div>
                  
                  <div className="contact-method">
                    <div className="method-icon">
                      <MapPin size={24} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="heading-3">Locations</h3>
                      <p className="body-medium">
                        <Link to="/locations/coffs-harbour" className="inline-link">Coffs Harbour</Link> | <Link to="/clinics" className="inline-link">Port Macquarie</Link> | <Link to="/clinics" className="inline-link">Sydney</Link> | <Link to="/clinics" className="inline-link">Melbourne</Link> | <Link to="/clinics" className="inline-link">Gold Coast</Link> | <Link to="/clinics" className="inline-link">Hamilton VIC</Link> | <Link to="/locations/telehealth" className="inline-link">Telehealth</Link>
                      </p>
                    </div>
                  </div>

                  <div className="contact-method">
                    <div className="method-icon">
                      <Clock size={24} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="heading-3">Contact Hours</h3>
                      <p className="body-small">
                        Monday - Friday: 9:00 AM - 5:00 PM<br />
                        Saturday: we will respond within 24 hours of request<br />
                        Sunday: we will respond within 24 hours of request
                      </p>
                    </div>
                  </div>
                </div>

                <div className="upload-docs-cta" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-light)' }}>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="btn btn-secondary"
                    data-testid="upload-docs-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', width: '100%', justifyContent: 'center' }}
                  >
                    <Upload size={18} />
                    Upload Client Documents
                  </button>
                  <p className="body-small" style={{ marginTop: '0.5rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                    Upload school reports, referral letters or other documents securely
                  </p>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <div className="card">
                <div className="form-header">
                  <Send size={32} strokeWidth={1.5} />
                  <h2 className="heading-2">Send Us a Message</h2>
                  <p className="body-medium">
                    Fill out the form below and we'll get back to you within 24 hours
                  </p>
                </div>
                
                <div className="form-content">
                  {submitSuccess ? (
                    <div className="success-message">
                      <CheckCircle size={48} className="success-icon" />
                      <h3>Message Sent Successfully!</h3>
                      <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="contact-form">
                      {formsOff && <PreviewFormNotice />}
                      <div className="privacy-notice-top">
                        <p className="body-small" style={{ fontStyle: 'italic', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                          We take your privacy seriously. All data and files you submit are securely stored in Australia in compliance with The Privacy Act (1988) and the Australian Privacy Principles (APPs). You can find full details in our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Privacy Policy</a>.
                        </p>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="firstName">First Name *</label>
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                            disabled={submitting}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="lastName">Last Name *</label>
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                            disabled={submitting}
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="email">Email Address *</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            disabled={submitting}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="phoneNumber">Phone Number</label>
                          <input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                            disabled={submitting}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="contactPreference">Preferred Contact Method *</label>
                        <select
                          id="contactPreference"
                          name="contactPreference"
                          value={formData.contactPreference}
                          onChange={handleInputChange}
                          required
                          disabled={submitting}
                        >
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                          <option value="sms">SMS</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="bestTimeToContact">Best Time to Contact</label>
                        <input
                          type="text"
                          id="bestTimeToContact"
                          name="bestTimeToContact"
                          value={formData.bestTimeToContact}
                          onChange={handleInputChange}
                          placeholder="e.g., Weekday mornings, After 6 PM"
                          disabled={submitting}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="reasonForContact">What is this enquiry about?</label>
                        <select
                          id="reasonForContact"
                          name="reasonForContact"
                          value={formData.reasonForContact}
                          onChange={handleInputChange}
                          disabled={submitting}
                          data-testid="reason-for-contact-select"
                        >
                          <option value="">-- Please select --</option>
                          <option value="Autism assessment">Autism assessment</option>
                          <option value="ADHD assessment">ADHD assessment</option>
                          <option value="FASD assessment">FASD assessment</option>
                          <option value="Learning assessment">Learning assessment</option>
                          <option value="Intellectual disability assessment">Intellectual disability assessment</option>
                          <option value="Assessment">Assessment (other/not sure)</option>
                          <option value="Therapy">Therapy</option>
                          <option value="School partnership">School partnership</option>
                          <option value="Referral from a health professional">Referral from a health professional</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="message">Message *</label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          rows="5"
                          required
                          disabled={submitting}
                          placeholder="Please tell us how we can help you..."
                        ></textarea>
                      </div>

                      {submitError && (
                        <div className="error-message">
                          <p>{submitError}</p>
                        </div>
                      )}

                      <div className="form-footer">
                        <p className="body-small consent-text">
                          By submitting this form, you confirm that you have read and agree to our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                        </p>
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={submitting || formsOff}
                        >
                          {submitting ? (
                            <>
                              <span className="spinner"></span>
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send size={20} />
                              Send Message
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Support Section */}
      <CrisisSupport />

      {/* Upload Documents Modal */}
      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={() => setShowUploadModal(false)} data-testid="upload-modal">
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <button className="upload-modal-close" onClick={() => setShowUploadModal(false)} aria-label="Close">
              <X size={24} />
            </button>
            <div className="upload-modal-header">
              <Upload size={28} strokeWidth={1.5} />
              <h2 className="heading-2">Upload Client Documents</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                Please upload your documents here. All files are stored securely in Australia.
              </p>
            </div>
            {uploadSuccess ? (
              <div className="success-message" style={{ padding: '2rem', textAlign: 'center' }}>
                <CheckCircle size={48} className="success-icon" />
                <h3>Document Uploaded Successfully</h3>
                <p>Thank you. Your documents have been received.</p>
              </div>
            ) : (
              <form onSubmit={handleUploadSubmit} className="contact-form">
                {formsOff && <PreviewFormNotice />}
                <div className="form-group">
                  <label htmlFor="uploadClientName">Client Name *</label>
                  <input
                    type="text"
                    id="uploadClientName"
                    value={uploadData.clientName}
                    onChange={(e) => setUploadData(prev => ({ ...prev, clientName: e.target.value }))}
                    required
                    disabled={uploading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="uploadEmail">Email Address *</label>
                  <input
                    type="email"
                    id="uploadEmail"
                    value={uploadData.contactEmail}
                    onChange={(e) => setUploadData(prev => ({ ...prev, contactEmail: e.target.value }))}
                    required
                    disabled={uploading}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="uploadNotes">Notes (optional)</label>
                  <textarea
                    id="uploadNotes"
                    value={uploadData.notes}
                    onChange={(e) => setUploadData(prev => ({ ...prev, notes: e.target.value }))}
                    disabled={uploading}
                    rows={2}
                    placeholder="e.g. school report for Sarah, paediatrician letter"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="uploadFile">Document *</label>
                  <input
                    type="file"
                    id="uploadFile"
                    onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files[0] }))}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.mov,.avi,.wmv,.mkv,.webm"
                    disabled={uploading}
                    className="file-input"
                  />
                  <p className="body-small" style={{ marginTop: '0.25rem', color: 'var(--text-muted)' }}>
                    Accepted formats: PDF, Word, images or video (max 100MB)
                  </p>
                </div>
                {uploadError && (
                  <div className="error-message"><p>{uploadError}</p></div>
                )}
                <button type="submit" className="btn btn-primary" disabled={uploading || formsOff} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  {uploading ? (
                    <><span className="spinner"></span> Uploading...</>
                  ) : (
                    <><Upload size={18} /> Upload Document</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;
